import { Flex } from '@chakra-ui/layout'
import * as History from 'history'
import { Base64 } from 'js-base64'
import { isEqual } from 'lodash'
import { MutableRefObject, useMemo } from 'react'
import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { encodeStringForUrl } from '../../../utils/mediaLibrary'
import {
  MLHeaderActionbar,
  MLHeaderToolbar,
} from '../../components/MediaLibrary'
import { ContextMenu } from '../../components/MediaLibrary/ContextMenu'
import Panel from '../../components/MediaLibrary/Panel'
import PollOperations from '../../components/MediaLibrary/PollOperations'
import PollUnseenNotificationsCount from '../../components/MediaLibrary/Notifications/PollUnseenNotificationsCount'
import CollectionsView from '../../components/MediaLibrary/View/CollectionsView'
import { FileType } from '../../constants'
import { MLContext } from '../../providers'
import {
  defaultInitialState as defaultMLState,
  MediaLibraryActions,
} from '../../providers/MLProvider'
import { RouterBackStackContext } from '../../providers/RouterBackStackProvider'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import { MouseDragSelect } from '../../components/MouseDragSelect'
import { Helmet } from 'react-helmet-async'
import { ViewableParentSuccessResponse } from '../../interfaces'
import { useTranslation } from 'react-i18next'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import GenerateImageUsingAIBannerCTA from '../../components/MediaLibrary/GenerateImage/BannerCTA'


const MLMainContentHeadingBox = styled(MainContentHeadingBox)`
  min-height: initial;
  max-height: initial;
  padding-top: 1rem;
  padding-bottom: 1rem;
`

const MediaCollectionsLayout = (): React.ReactElement => {
  const routerHistory = useHistory()
  const routerLocation = useLocation()
  const backStack = useContext(RouterBackStackContext)


  const { t } = useTranslation()

  const { isPublicLink } = useContext(PublicLinksInfoContext)

  const [state, dispatch] = useContext(MLContext)

  const mouseSelectionRef = useRef<HTMLDivElement | null>(null)
  const mediaLibraryContentWrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    mediaLibraryContentWrapperRef.current?.addEventListener(
      'click',
      (e): void => {
        if ((e.target as HTMLElement).tagName === 'DIV') {
          dispatch([
            { type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS },
            {
              type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
            },
          ])
        }
      },
    )

    return () => {
      mediaLibraryContentWrapperRef.current?.removeEventListener(
        'click',
        (e): void => {
          if ((e.target as HTMLElement).tagName === 'DIV') {
            dispatch([
              { type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS },
              {
                type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
              },
            ])
          }
        },
      )
    }
  }, [mediaLibraryContentWrapperRef.current])

  const resetSearch = useCallback(() => {
    dispatch([
      {
        type: MediaLibraryActions.RESET_MEDIA_COLLECTION_SEARCH,
      },
      {
        type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
        payload: false,
      },
      {
        type: MediaLibraryActions.SET_SEARCH_SEMANTIC_VALUE,
        payload: '',
      },
      {
        type: MediaLibraryActions.SET_SEARCH_SEMANTIC_URL_VALUE,
        payload: '',
      },
      {
        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
      },
      {
        type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
        payload: false,
      },
    ])
  }, [state.searchQuery])

  const title = useMemo(() => {
    if (state.searchQuery) {
      return t('search_results', 'Search Results')
    }

    if (
      !state.currentMediaCollection &&
      !(state.viewableParent as ViewableParentSuccessResponse).mediaCollection
    ) {
      return t('media_collections', 'Media collections')
    }

    const folder = state.folderPath.split('/').pop()

    return [
      (state.viewableParent as ViewableParentSuccessResponse).mediaCollection
        ?.name,
      folder,
    ]
      .filter(x => x)
      .join(' / ')
  }, [
    state.folderPath,
    state.currentMediaCollection,
    state.viewableParent,
    state.searchQuery,
  ])

  return (
    <>
      <Helmet title={title} />
      <Flex
        direction="column"
        alignItems="stretch"
        height="100%"
        overflow="auto"
        ref={mouseSelectionRef}
      >
        <MouseDragSelect containerRef={mouseSelectionRef} />
        <MLMainContentHeadingBox>
          <MLHeaderActionbar resetSearch={resetSearch} />
        </MLMainContentHeadingBox>
        <MLHeaderToolbar resetSearch={resetSearch} />
        <MainContentWrapper ref={mediaLibraryContentWrapperRef}>
          {!isPublicLink && <GenerateImageUsingAIBannerCTA />}
          <ContextMenu />
          <CollectionsView />
        </MainContentWrapper>
        <PollOperations />
        {!isPublicLink && <PollUnseenNotificationsCount />}
      </Flex>
      <Panel />
    </>
  )
}

export default MediaCollectionsLayout
